import { Injectable, inject } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { OAuthService } from 'angular-oauth2-oidc';
import { SessionMonitorService } from '../services/session-monitor.service';

@Injectable({
  providedIn: 'root'
})
export class CommonInterceptor implements HttpInterceptor {
  private readonly sessionMonitorService = inject(SessionMonitorService);

  constructor(private readonly oauthService: OAuthService) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip token injection for certain URLs (e.g., auth endpoints)
    const skipUrls = ['/auth/login', '/oauth/', '/token', '/realms/'];
    const shouldSkip = skipUrls.some(url => req.url.includes(url));
    
    if (shouldSkip) {
      return next.handle(req);
    }
    

    // Get the access token from OAuthService with proper validation
    const accessToken = this.getValidAccessToken();

    // If we have a valid access token, clone the request and add the Authorization header
    if (accessToken) {
      const authReq = req.clone({
        headers: req.headers.set('Authorization', `Bearer ${accessToken}`)
      });
      
      return next.handle(authReq).pipe(
        catchError((error: HttpErrorResponse) => {
          // If 401 error, redirect to login
          if (error.status === 401) {
            console.warn('401 Unauthorized - redirecting to login');
            this.sessionMonitorService.logout();
            return throwError(() => error);
          }
          return throwError(() => error);
        })
      );
    }

    // If no valid token, proceed with the original request
    return next.handle(req);
  }

  /**
   * Gets a valid access token from OAuth service with fallback strategies
   */
  private getValidAccessToken(): string | null {
    // Strategy 1: Check if OAuth service has a valid access token
    if (this.oauthService.hasValidAccessToken()) {
      return this.oauthService.getAccessToken();
    }

    // Strategy 2: Try to get token directly from OAuth service (might be valid but not detected)
    const token = this.oauthService.getAccessToken();
    if (token && this.isTokenValid(token)) {
      return token;
    }

    // Strategy 3: Force OAuth service to re-read from storage
    try {
      this.oauthService.tryLoginImplicitFlow();
      if (this.oauthService.hasValidAccessToken()) {
        return this.oauthService.getAccessToken();
      }
    } catch (error) {
      // OAuth service error - continue to next strategy
      console.debug('OAuth service tryLoginImplicitFlow failed:', error);
    }

    // Strategy 4: Direct localStorage access as last resort
    const directToken = localStorage.getItem('access_token');
    if (directToken && this.isTokenValid(directToken)) {
      return directToken;
    }

    return null;
  }

  /**
   * Basic token validation with small buffer for safety
   */
  private isTokenValid(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      const bufferTime = 10000; // 10 seconds buffer to prevent edge cases
      return exp > (now + bufferTime);
    } catch (error) {
      // Token parsing error - treat as invalid
      console.debug('Token validation failed:', error);
      return false;
    }
  }
}
